import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { CompositeIndexApiClient } from 'api';
import { CompositeIndexDisplayName, CompositeIndexModel } from 'models';
import { CompositeIndexType } from 'models/CompositeIndex/CompositeIndex';

const COMPOSITE_INDEX_QUERY_KEY = (type: CompositeIndexType) => ['composite-index', type];

export const useCompositeEditionIndex = () => {
  const type = CompositeIndexType.EDITION_INDEX;
  return useQuery<CompositeIndexModel>({
    queryKey: COMPOSITE_INDEX_QUERY_KEY(type),
    queryFn: () => CompositeIndexApiClient.getCompositeIndexByType(type),
  });
};

export const useRefreshCompositeEditionIndex = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const type = CompositeIndexType.EDITION_INDEX;

  return useMutation({
    mutationFn: () => CompositeIndexApiClient.refreshCompositeIndex(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPOSITE_INDEX_QUERY_KEY(type) });
      toast({
        title: 'Composite index refresh queued',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to refresh composite index',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
};

export const useRegenerateCompositeEditionIndex = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const type = CompositeIndexType.EDITION_INDEX;
  const name = CompositeIndexDisplayName[type];

  return useMutation({
    mutationFn: () => CompositeIndexApiClient.regenerateCompositeIndex(name, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPOSITE_INDEX_QUERY_KEY(type) });
      toast({
        title: 'Composite index regeneration started',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to regenerate composite index',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
};
